import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { PROCESS_TYPE } from 'src/shared/constants/constants'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { Alert, Box, IconButton, Stack, Typography } from '@mui/material'
import { EditIcon, MoveDownIcon, MoveUpIcon } from 'src/shared/icons/Icons'
import { S } from 'src/shared/styled/S'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useUserLocale } from 'src/user/UserContext'
import {
  fetchProcessCommitteeConfigSorted,
  updateSortOnCommitteeConfig,
} from 'src/screens/administration/configuration-management/committees-configuration/committeeConfigurationQueries'
import {
  Committee_Config,
  Committee_Config_Bool_Exp,
  Committee_Config_Set_Input,
  Mutation_Root,
  Query_Root,
} from 'src/@types/graphql'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { AddButton } from 'src/shared/button/Buttons'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { debounce } from 'lodash'

interface CommitteeConfigurationGridProps {
  process: PROCESS_TYPE
}

export const CommitteeConfigurationSortList = (props: CommitteeConfigurationGridProps) => {
  const { getMessage } = useMessageSource()
  const urqlClient = useClient()
  const notificationService = useNotificationService()
  const locale = useUserLocale()
  const navigate = useDelayedNavigate()

  const [committeeConfig, setCommitteeConfig] = useState<Array<Committee_Config>>([])
  const [isReorderingLocked, setIsReorderingLocked] = useState(false)

  const [totalNumber, setTotalNumber] = useState<number>()

  // fetch data
  const fetchCommittees = useCallback(async () => {
    const committeeWhereClause: Committee_Config_Bool_Exp = {
      _and: [{ processes: { _contains: props.process } }],
    }

    const { data, error } = await urqlClient
      .query<
        {
          committee_config: Query_Root['committee_config']
          committee_config_aggregate: Query_Root['committee_config_aggregate']
        },
        { committeeWhereClause: Committee_Config_Bool_Exp }
      >(fetchProcessCommitteeConfigSorted, { committeeWhereClause: committeeWhereClause })
      .toPromise()

    if (!error && data && data.committee_config?.length > 0 && data.committee_config_aggregate) {
      setCommitteeConfig(data.committee_config)
      setTotalNumber(data.committee_config_aggregate.aggregate?.count ?? 0)
    } else {
      notificationService.operationFailed()
    }

    setIsReorderingLocked(false)
  }, [getMessage, notificationService, props.process, urqlClient])

  useEffect(() => {
    fetchCommittees()
  }, [fetchCommittees])

  // handle events
  const onEditClick = (e: React.MouseEvent<HTMLButtonElement>, committeeConfigId: number) => {
    e.stopPropagation()

    navigate(
      ROUTES.CommitteesConfigurationIndexEdit.params({
        processType: props.process,
        committeeId: committeeConfigId,
      }),
    )
  }

  const onAddClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()

    navigate(ROUTES.CommitteesConfigurationIndexCreate.params({ processType: props.process }))
  }

  const updateCommitteePosition = useCallback(
    async (committee1: Committee_Config, committee2: Committee_Config) => {
      if (committee1.sort_number === committee2.sort_number) {
        return Promise.reject(new Error())
      }

      const setClauseCriteria1: Committee_Config_Set_Input = {
        sort_number: committee2.sort_number,
      }

      const setClauseCriteria2: Committee_Config_Set_Input = {
        sort_number: committee1.sort_number,
      }

      const updateFirstWhere: Committee_Config_Bool_Exp = {
        id: { _eq: committee1.id },
      }

      const updateSecondWhere: Committee_Config_Bool_Exp = {
        id: { _eq: committee2.id },
      }

      return await urqlClient
        .mutation<
          {
            updateFirst: Mutation_Root['update_criteria_sorting_config']
            updateSecond: Mutation_Root['update_criteria_sorting_config']
          },
          {
            firstWhere: Committee_Config_Bool_Exp
            secondWhere: Committee_Config_Bool_Exp
            setClause1: Committee_Config_Set_Input
            setClause2: Committee_Config_Set_Input
          }
        >(updateSortOnCommitteeConfig, {
          firstWhere: updateFirstWhere,
          secondWhere: updateSecondWhere,
          setClause1: setClauseCriteria1,
          setClause2: setClauseCriteria2,
        })
        .toPromise()
    },
    [urqlClient],
  )

  const debouncedHandleReorder = useMemo(
    () =>
      debounce(
        async (reorderFunction: (...args: any[]) => Promise<void>, ...args: any[]) => {
          if (isReorderingLocked) return

          setIsReorderingLocked(true)

          try {
            await reorderFunction(...args)
            await new Promise((resolve) => setTimeout(resolve, 500))
          } catch (e) {
            setIsReorderingLocked(false)
            console.error('Error caught in committee sort debounce handler reorder method')
          }
        },
        500,
        { leading: true, trailing: false },
      ),
    [],
  )

  const onSwitchCommitteePositionInternal = useCallback(
    async (committee1: Committee_Config, committee2: Committee_Config) => {
      try {
        await updateCommitteePosition(committee1, committee2)
        await fetchCommittees()
      } catch (e) {
        notificationService.operationFailed()
        throw e
      }
    },
    [notificationService, fetchCommittees, updateCommitteePosition],
  )

  const onSwitchCommitteePosition = useCallback(
    async (committee1: Committee_Config, committee2: Committee_Config) => {
      await debouncedHandleReorder(onSwitchCommitteePositionInternal, committee1, committee2)
    },
    [debouncedHandleReorder, onSwitchCommitteePositionInternal],
  )

  return (
    <ScreenLayout
      title={`${getMessage('label.navigation.configuration.committees')} ${getMessage('label.configuration')}`}
    >
      <PageLayout>
        <Stack spacing={2}>
          <Box>
            <Alert severity="warning">{getMessage('label.warning.config.edit')}</Alert>
          </Box>

          <Box>
            <Box display={'flex'} justifyContent={'space-between'} pb={1}>
              {committeeConfig.length > 0 && (
                <Typography variant="subtitle1" alignSelf={'end'}>
                  {`${getMessage('label.configuration.grid.toolbar.results')}: ${totalNumber ?? ''}`}
                </Typography>
              )}

              <Box marginLeft="auto">
                <AddButton
                  messageKey="label.configuration.committee.create.button"
                  startIcon={<AddOutlinedIcon />}
                  onClick={onAddClick}
                />
              </Box>
            </Box>

            {committeeConfig.map((cc, cIndex) => {
              return (
                <Box mt={1} key={cc.id}>
                  <S.ConfigurationSortCard.Card greyedOut={!cc.active}>
                    <Box className="header-wrapper">
                      <Box className="header-content">
                        <S.ConfigurationSortCard.Header
                          avatar={
                            <S.ConfigurationSortCard.Box>
                              <S.ConfigurationSortCard.IdChip size="small" label={cIndex + 1} />
                              <S.ConfigurationSortCard.MoveButtons>
                                <IconButton
                                  color="inherit"
                                  disabled={cIndex === 0 || isReorderingLocked}
                                  onClick={() =>
                                    onSwitchCommitteePosition(committeeConfig[cIndex], committeeConfig[cIndex - 1])
                                  }
                                  size="large"
                                >
                                  <MoveUpIcon />
                                </IconButton>
                                <IconButton
                                  color="inherit"
                                  disabled={cIndex === committeeConfig.length - 1 || isReorderingLocked}
                                  onClick={() =>
                                    onSwitchCommitteePosition(committeeConfig[cIndex], committeeConfig[cIndex + 1])
                                  }
                                  size="large"
                                >
                                  <MoveDownIcon />
                                </IconButton>
                              </S.ConfigurationSortCard.MoveButtons>
                            </S.ConfigurationSortCard.Box>
                          }
                          title={<Typography variant="subtitle2">{cc.descriptions[locale]}</Typography>}
                          action={
                            <IconButton
                              sx={{ m: 0, mr: -2 }}
                              onClick={(e) => onEditClick(e, cc.id)}
                              color="primary"
                              size={'small'}
                            >
                              <EditIcon fontSize={'small'} />
                            </IconButton>
                          }
                          disableTypography
                        />
                      </Box>
                    </Box>
                  </S.ConfigurationSortCard.Card>
                </Box>
              )
            })}
          </Box>
        </Stack>
      </PageLayout>
    </ScreenLayout>
  )
}
